import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Autocomplete,
  TextField,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import { apiRequest } from "../config/api";
import { useSeason } from "../contexts/SeasonContext";
import { BadgeType, Player } from "../types";

interface BadgeInfo {
  name: string;
  emoji: string;
}

const BADGE_CATALOGUE: Record<BadgeType, BadgeInfo> = {
  first_win: { name: "First Victory", emoji: "🎉" },
  giant_killer: { name: "Giant Killer", emoji: "⚔️" },
  win_streak_10: { name: "Unstoppable", emoji: "🔥" },
  close_call: { name: "Close Call", emoji: "💪" },
  perfect_day: { name: "Perfect Day", emoji: "⭐" },
  veteran: { name: "Veteran", emoji: "🏅" },
  partner_carry: { name: "Partner Carry", emoji: "🎯" },
  clean_sweep: { name: "Clean Sweep", emoji: "🧹" },
  mr_ms_consistent: { name: "Mr/Ms Consistent", emoji: "🎲" },
  century: { name: "Century", emoji: "💯" },
  iron_man: { name: "Iron Man", emoji: "🦾" },
  division_hopper: { name: "Division Hopper", emoji: "🔀" },
  unlucky: { name: "Unlucky", emoji: "😅" },
  all_season_warrior: { name: "All-Season Warrior", emoji: "⚔️" },
  division_champion: { name: "Division Champion", emoji: "👑" },
  sp_team: { name: "SP Team", emoji: "🏆" },
};

const getDivisionDisplayName = (division: string): string =>
  division === "Women Division" ? "Women Division 1" : division;

const badgeEarnedAtToDate = (earnedAt: any): Date => {
  if (earnedAt && typeof earnedAt === "object" && "_seconds" in earnedAt) {
    return new Date(earnedAt._seconds * 1000);
  }
  if (earnedAt && typeof earnedAt === "object" && "toDate" in earnedAt) {
    return earnedAt.toDate();
  }
  return new Date(earnedAt);
};

function PlayerBadges() {
  const { allSeasons } = useSeason();
  const [players, setPlayers] = useState<Player[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [addType, setAddType] = useState<BadgeType | "">("");
  const [addSeasonId, setAddSeasonId] = useState<string>("");
  const [adding, setAdding] = useState(false);

  const [removeTarget, setRemoveTarget] = useState<any | null>(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    fetchPlayers();
  }, []);

  useEffect(() => {
    setAddType("");
    setAddSeasonId((selectedPlayer as any)?.currentSeason || "");
  }, [selectedPlayer?.id]);

  const fetchPlayers = async () => {
    setLoadingPlayers(true);
    try {
      const response = await apiRequest("/api/players");
      if (response.ok) {
        const data = await response.json();
        setPlayers(data);
      }
    } catch {
      setError("Failed to load players");
    } finally {
      setLoadingPlayers(false);
    }
  };

  const refreshSelectedPlayer = async (playerId: string) => {
    const response = await apiRequest(`/api/players/${playerId}`);
    if (response.ok) {
      const data = await response.json();
      setSelectedPlayer(data);
      setPlayers((prev) => prev.map((p) => (p.id === playerId ? data : p)));
    }
  };

  const handleAddBadge = async () => {
    if (!selectedPlayer || !addType) return;
    setAdding(true);
    setError("");
    try {
      const response = await apiRequest(`/api/players/${selectedPlayer.id}/badges`, {
        method: "POST",
        body: JSON.stringify({ type: addType, seasonId: addSeasonId || undefined }),
      });
      if (response.ok) {
        const data = await response.json();
        setSuccess(`Added "${BADGE_CATALOGUE[addType as BadgeType]?.name}" (+${data.pointsAwarded} pts)`);
        setAddType("");
        await refreshSelectedPlayer(selectedPlayer.id);
      } else {
        const errData = await response.json();
        setError(errData.error || "Failed to add badge");
      }
    } catch {
      setError("Failed to add badge");
    } finally {
      setAdding(false);
    }
  };

  const handleConfirmRemoveBadge = async () => {
    if (!selectedPlayer || !removeTarget) return;
    setRemoving(true);
    setError("");
    try {
      const response = await apiRequest(`/api/players/${selectedPlayer.id}/badges/remove`, {
        method: "POST",
        body: JSON.stringify({
          type: removeTarget.type,
          seasonId: removeTarget.seasonId,
          earnedAt: badgeEarnedAtToDate(removeTarget.earnedAt).toISOString(),
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setSuccess(
          `Removed "${BADGE_CATALOGUE[removeTarget.type as BadgeType]?.name || removeTarget.type}" (-${data.pointsRemoved} pts)`
        );
        setRemoveTarget(null);
        await refreshSelectedPlayer(selectedPlayer.id);
      } else {
        const errData = await response.json();
        setError(errData.error || "Failed to remove badge");
      }
    } catch {
      setError("Failed to remove badge");
    } finally {
      setRemoving(false);
    }
  };

  const sortedBadges = useMemo(() => {
    if (!selectedPlayer?.badges) return [];
    return [...selectedPlayer.badges].sort(
      (a, b) => badgeEarnedAtToDate(b.earnedAt).getTime() - badgeEarnedAtToDate(a.earnedAt).getTime()
    );
  }, [selectedPlayer]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <MilitaryTechIcon color="primary" />
        <Typography variant="h4">Player Badges</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manually add or remove badges for a player. Adding a badge credits its point value to the
        player's score; removing one deducts it. Use this to correct badges after fixing a scored
        match.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Autocomplete
          options={players}
          getOptionLabel={(p) => `${p.name} (${getDivisionDisplayName(p.division)})`}
          loading={loadingPlayers}
          value={selectedPlayer}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, value) => setSelectedPlayer(value)}
          renderInput={(params) => <TextField {...params} label="Search for a player" />}
        />
      </Paper>

      {selectedPlayer && (
        <>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6">{selectedPlayer.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {getDivisionDisplayName(selectedPlayer.division)} &nbsp;|&nbsp; Total points:{" "}
                {selectedPlayer.totalPoints}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Add a Badge
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel>Badge type</InputLabel>
                  <Select
                    value={addType}
                    label="Badge type"
                    onChange={(e) => setAddType(e.target.value as BadgeType)}
                  >
                    {(Object.keys(BADGE_CATALOGUE) as BadgeType[]).map((t) => (
                      <MenuItem key={t} value={t}>
                        {BADGE_CATALOGUE[t].emoji} {BADGE_CATALOGUE[t].name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Season</InputLabel>
                  <Select value={addSeasonId} label="Season" onChange={(e) => setAddSeasonId(e.target.value)}>
                    <MenuItem value="">No season</MenuItem>
                    {allSeasons.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name} {s.isActive ? "(Active)" : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  startIcon={adding ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                  disabled={!addType || adding}
                  onClick={handleAddBadge}
                >
                  Add Badge
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Current Badges ({sortedBadges.length})
              </Typography>
              {sortedBadges.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No badges yet.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Badge</TableCell>
                      <TableCell>Season</TableCell>
                      <TableCell align="right">Points</TableCell>
                      <TableCell>Earned</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedBadges.map((badge: any, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <span>{BADGE_CATALOGUE[badge.type as BadgeType]?.emoji || "🏆"}</span>
                            {BADGE_CATALOGUE[badge.type as BadgeType]?.name || badge.type}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {badge.seasonId
                            ? allSeasons.find((s) => s.id === badge.seasonId)?.name || badge.seasonId
                            : "—"}
                        </TableCell>
                        <TableCell align="right">{badge.pointsAwarded ?? 0}</TableCell>
                        <TableCell>{badgeEarnedAtToDate(badge.earnedAt).toLocaleDateString()}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Remove badge">
                            <IconButton size="small" color="error" onClick={() => setRemoveTarget(badge)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={!!removeTarget} onClose={() => !removing && setRemoveTarget(null)}>
        <DialogTitle>Remove Badge?</DialogTitle>
        <DialogContent>
          {removeTarget && (
            <Typography variant="body2">
              Remove "{BADGE_CATALOGUE[removeTarget.type as BadgeType]?.name || removeTarget.type}" from{" "}
              {selectedPlayer?.name}? This will deduct {removeTarget.pointsAwarded ?? 0} point(s) from
              the matching season's stats.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)} disabled={removing}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmRemoveBadge}
            disabled={removing}
            startIcon={removing ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PlayerBadges;
